<template>
  <div class="admin-html-images">
    <h1>圖片管理</h1>

    <!-- 搜尋列 -->
    <div class="search-bar">
      <input
        v-model="keyword"
        type="text"
        placeholder="輸入關鍵字搜尋"
        @keyup.enter="handleSearch"
      />
      <button class="btn-search" @click="handleSearch">搜尋</button>
    </div>

    <!-- 若有資料：用 table 或 div 列出 htmlimages -->
    <div v-if="htmlimages.length" class="table-container">
      <table>
        <thead>
          <tr>
            <th style="width: 240px;">標題</th>
            <th>圖片圖示</th>
            <th>圖片網址</th>
            <th style="width: 120px;">操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(image, index) in htmlimages" :key="index">
            <td>{{ image.title }}</td>
            <td>
              <img :src="image.image_url" alt="圖片" style="max-width: 100px;" />
            </td>
            <td> 
              <input
            type="text"
            v-model="image.image_url"
            class="input-field"
            placeholder="請輸入圖片網址"
          />
            </td>
            <td>
              <button class="btn-view" @click="updateUrl(image)">更新</button>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- 分頁區 -->
      <div class="pagination">
        <button
          :disabled="page <= 1"
          @click="goToPage(page - 1)"
        >
          上一頁
        </button>
        <span class="page-info">
          第 {{ page }} / {{ totalPages }} 頁
        </span>
        <button
          :disabled="page >= totalPages"
          @click="goToPage(page + 1)"
        >
          下一頁
        </button>
      </div>
    </div>

    <!-- 若無資料 -->
    <div v-else>
      <p>沒有任何圖片資料</p>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useAdminSysTempStore } from '@/store/adminSysTempStore.js'
import axios from 'axios'
import Swal from 'sweetalert2'

const router = useRouter()

// 資料 / 分頁相關
const htmlimages = ref([])
const keyword = ref('')
const limit = ref(30)
const offset = ref(0)
const page = ref(1)
const total = ref(0)
const totalPages = ref(1)

/** 從後端抓取 HTML 圖片 */
async function fetchHtmlImages() {
  try {
    const { data } = await axios.post('/api/admin/htmlimages', {
      limit: limit.value,       // 每頁筆數
      offset: offset.value,     // 跳過多少筆 (用於分頁)
      keyword: keyword.value,   // 搜尋關鍵字
    })
    if (data.success) {
      htmlimages.value = data.htmlimages  // 真正的資料陣列
      total.value = data.total          // 總筆數
      totalPages.value = Math.ceil(total.value / limit.value) || 1
    } else {
      console.error(data.message)
    }
  } catch (err) {
    console.error('取得 HTML Images 資料失敗:', err)
  }
}

/** 搜尋：重置 offset/page 並重新抓取 */
function handleSearch() {
  page.value = 1
  offset.value = 0
  fetchHtmlImages()
}

/** 切換分頁 */
function goToPage(newPage) {
  // 確保不超出範圍
  if (newPage < 1 || newPage > totalPages.value) return
  page.value = newPage
  offset.value = (newPage - 1) * limit.value
  fetchHtmlImages()
}

/** 檢視(編輯)：在新視窗打開該 HTML 圖片 */
async function updateUrl(image) {
  try {
        const { data } = await axios.post('/api/admin/updateImageUrl', {
          version_page_type: image.version_page_type,
          image_type: image.image_type,
          image_url: image.image_url,
        })
        if (data.success) {
          Swal.fire('更新成功', '', 'success')
        } else {
          Swal.fire('更新失敗', data.message, 'error')
        }
      } catch (err) {
        console.error('更新 HTML Image 失敗:', err)
        Swal.fire('更新失敗', '請稍後再試', 'error')
      }

}

onMounted(async () => {
  // 檢查是否登入
  const useAdminSysTemp = useAdminSysTempStore()
  await useAdminSysTemp.fetchAdminCheckLogin()
  if (!useAdminSysTemp.isLoggedIn) {
    router.push('/admin/login')
    return
  }
  console.log('AdminHtmlImagesPage mounted')
  await fetchHtmlImages()
})
</script>

<style scoped>
.admin-html-images {
  background-color: #fafafa;
  padding: 1rem;
  border-radius: 6px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  max-width: 900px; /* 讓畫面更集中 */
  margin: 0 auto;
}

/* 搜尋欄 */
.search-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 1rem;
}

.search-bar input {
  flex: 1;
  padding: 8px;
  font-size: 1rem;
}

.btn-search {
  background-color: #007bff;
  color: #fff;
  padding: 8px 14px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}
.btn-search:hover {
  background-color: #0056b3;
}

/* 表格樣式 */
.table-container {
  overflow-x: auto; /* 若欄位太多時可橫向捲動 */
}

table {
  width: 100%;
  border-collapse: collapse;
  background-color: #fff;
}

thead {
  background-color: #f2f2f2;
}

th,
td {
  text-align: left;
  padding: 12px;
  border: 1px solid #e0e0e0;
}

/* 分頁區 */
.pagination {
  display: flex;
  align-items: center;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 12px;
}

.pagination button {
  background-color: #2e3a59;
  color: #fff;
  padding: 6px 12px;
  border: none;
  cursor: pointer;
  border-radius: 4px;
}

.pagination button:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.page-info {
  font-weight: bold;
}

/* 按鈕 */
.btn-view {
  padding: 6px 12px;
  border: none;
  background-color: #28a745;
  color: #fff;
  border-radius: 4px;
  cursor: pointer;
}

.btn-view:hover {
  background-color: #218838;
}

/* ----------------------------------
   手機RWD (max-width: 768px)
   - 參考 Stacked Table
---------------------------------- */
@media (max-width: 768px) {

    /* 如果您把表頭隱藏 + tr/td block，想要每塊卡片置中 */
  .table-container table,
  .table-container table tbody,
  .table-container table tr,
  .table-container table td {
    display: block;
    width: 100%;
    margin: 0 auto; /* 讓「卡片」寬度 100%，本身居中 */
    text-align: center;
  }

  /* 也可在這裡再微調 td 的內邊距 / margin，讓每塊卡片更好看 */
  .table-container table td {
    padding: 8px 10px;
  }

  table thead {
    display: none; /* 隱藏表頭 */
  }

  table,
  table tbody,
  table tr,
  table td {
    display: block;
    width: 100%;
  }

  table tr {
    margin-bottom: 1rem;
    border-bottom: 2px solid #eee; /* 分隔每筆 */
  }

  /* 讓每個 td 自帶一行 label */
  table td {
    border: none;
    border-bottom: 1px solid #eee;
    position: relative;
    padding-left: 40%;
    min-height: 40px;
    box-sizing: border-box;
    white-space: pre-wrap;
  }

  /* 用 ::before 顯示 data-label (欄位名稱) */
  table td::before {
    content: attr(data-label);
    position: absolute;
    left: 12px;
    width: 35%;
    font-weight: bold;
    white-space: nowrap;
  }

  /* 讓 input 填滿在小螢幕可更易操作 */
  table td input[type="text"] {
    width: 100%;
  }
}

/* 基本輸入框設計 */
.input-field {
  display: block;             /* 讓 input 填滿整列 */
  width: 100%;                /* 寬度 100% */
  max-width: 100%;            /* 避免超過容器寬 */
  box-sizing: border-box;     /* 計算寬度時納入內邊距與邊框 */
  padding: 8px 12px;          /* 內距 (影響輸入區域大小) */
  font-size: 1rem;            /* 字體大小 */
  border: 1px solid #ccc;     /* 邊框 */
  border-radius: 4px;         /* 圓角 */
  transition: box-shadow 0.2s;
}

/* 聚焦時的高亮 */
.input-field:focus {
  outline: none;  /* 移除預設 focus 外框 */
  border-color: #007bff; 
  /* 或可加 box-shadow 做更明顯效果 */
  box-shadow: 0 0 4px rgba(0, 123, 255, 0.4);
}

/* 視需求可加 disabled 狀態等 */
.input-field[disabled] {
  background-color: #f2f2f2;
  cursor: not-allowed;
}


/* ----------------------------------
   手機 / 小螢幕 (RWD)
   在此稍微加大字體、內距
---------------------------------- */
@media (max-width: 768px) {
  .input-field {
    font-size: 1.1rem;  
    padding: 10px 14px;
  }
}

</style>